import type { ElementType } from "react";

import {
  ArrayIcon,
  AudienceIcon,
  ClickIcon,
  ColumnIcon,
  CubeIcon,
  FrameStackIcon,
  type IconBoxColors,
  type IconBoxVariant,
  SplitIcon,
  TableIcon,
  TraitIcon,
  VariableIcon,
} from "@hightouch/htui";

import { columnReferenceKey } from "src/components/explore/filter-popover/utils";
import type {
  ColumnOption,
  ColumnOptionCategory,
} from "src/formkit/components/formkit-context";
import type { AdditionalColumn, StaticValueColumn } from "src/types/visual";
import { getSearchRegExp } from "src/utils/string";

export type ColumnCategoryKind =
  | "all"
  | ColumnOptionCategory
  | "array-properties"
  | "generic";

export type ColumnCategory = {
  id: string;
  kind: ColumnCategoryKind;
  label: string;
  options: ColumnOption[];
  /** Mixed views show which model each option comes from. */
  showSource: boolean;
};

export type IconMeta = {
  icon: ElementType;
  color: IconBoxColors;
  variant?: IconBoxVariant;
};

export const COLUMN_CATEGORY_META: Record<ColumnCategoryKind, IconMeta> = {
  all: { icon: ColumnIcon, color: "gray" },
  model: { icon: CubeIcon, color: "indigo" },
  related: { icon: FrameStackIcon, color: "green" },
  "journey-event": { icon: ClickIcon, color: "teal", variant: "inverted" },
  traits: { icon: TraitIcon, color: "yellow" },
  "audience-traits": { icon: AudienceIcon, color: "yellow" },
  splits: { icon: SplitIcon, color: "blue" },
  "journey-context-variables": { icon: VariableIcon, color: "orange" },
  "array-properties": { icon: ArrayIcon, color: "gray" },
  generic: { icon: TableIcon, color: "gray" },
};

/** The source of a value that comes from the audience itself rather than a model. */
export const AUDIENCE_SOURCE_META: IconMeta = {
  icon: AudienceIcon,
  color: "blue",
};

const KIND_ORDER: ColumnCategoryKind[] = [
  "model",
  "related",
  "journey-event",
  "traits",
  "audience-traits",
  "splits",
  "journey-context-variables",
  "generic",
];

const MIXED_KINDS: ReadonlySet<ColumnCategoryKind> = new Set([
  "all",
  "traits",
  "audience-traits",
]);

/** Categories that list every column of their kind in one entry, whichever group holds it. */
const SHARED_CATEGORY_LABELS: Record<
  Exclude<ColumnOptionCategory, "model" | "related">,
  string
> = {
  "journey-event": "Entry event",
  traits: "Traits",
  "audience-traits": "Audience traits",
  splits: "Splits",
  "journey-context-variables": "Journey context variables",
};

type BuildColumnCategoriesOptions = {
  /** Rows inside an array mapping list the array's element properties instead of model columns. */
  mode: "columns" | "array-properties";
  isEventModel: boolean;
};

/**
 * Files each option under the category `mapColumns` marked it with, and turns a group of unmarked options, such as event properties, into one generic category.
 * There are no empty categories, except where typing a name into the search is the only way to map anything: nested array properties and an event model with no suggested properties yet.
 */
export function buildColumnCategories(
  columnOptions: ColumnOption[] | undefined,
  { mode, isEventModel }: BuildColumnCategoriesOptions,
): ColumnCategory[] {
  if (mode === "array-properties") {
    return [
      {
        id: "array-properties",
        kind: "array-properties",
        label: "Array properties",
        options: columnOptions ?? [],
        showSource: false,
      },
    ];
  }

  const groups = (columnOptions ?? []).filter(
    (group) => group.label !== "boosted",
  );
  const entries = new Map<
    string,
    { kind: ColumnCategoryKind; label: string; options: ColumnOption[] }
  >();
  const addOption = (
    id: string,
    kind: ColumnCategoryKind,
    label: string,
    option: ColumnOption,
  ) => {
    const entry = entries.get(id);
    if (entry) {
      entry.options.push(option);
    } else {
      entries.set(id, { kind, label, options: [option] });
    }
  };
  const bareOptions: ColumnOption[] = [];

  groups.forEach((group, index) => {
    if (!group.options) {
      bareOptions.push(group);
      return;
    }
    for (const option of group.options) {
      const kind = option.category;
      if (!kind) {
        addOption(`group:${index}`, "generic", group.label, option);
      } else if (kind === "model" || kind === "related") {
        // A group holds every column whose model has its name, so a merged model can share a group with a same-named model.
        addOption(
          `group:${index}:${kind}`,
          kind,
          group.entity?.name ?? group.label,
          option,
        );
      } else {
        addOption(kind, kind, SHARED_CATEGORY_LABELS[kind], option);
      }
    }
  });

  const categories = Array.from(entries, ([id, entry]) =>
    category(id, entry.kind, entry.label, entry.options),
  );
  if (bareOptions.length) {
    categories.push(category("bare", "generic", "Columns", bareOptions));
  }

  // Any category gives an event model a search to type a column name into, so the empty entry is needed only when there is none.
  if (!categories.length) {
    return isEventModel
      ? [category("event-properties", "generic", "Event properties", [])]
      : [];
  }

  categories.sort(
    (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind),
  );
  if (categories.length < 2) return categories;

  return [
    category(
      "all",
      "all",
      "All columns",
      categories.flatMap((c) => c.options),
    ),
    ...categories,
  ];
}

function category(
  id: string,
  kind: ColumnCategoryKind,
  label: string,
  options: ColumnOption[],
): ColumnCategory {
  return {
    id,
    kind,
    label,
    options,
    showSource:
      MIXED_KINDS.has(kind) && options.some((option) => option.entity),
  };
}

export function isAudienceTraitOption(option: ColumnOption): boolean {
  return option.category === "audience-traits";
}

/** The static value of an audience trait option, when its audience sets one. */
export function findStaticAudienceTrait(
  option: ColumnOption,
  additionalColumns: AdditionalColumn[] | undefined,
): StaticValueColumn | undefined {
  const optionValue = option.value;
  if (typeof optionValue !== "object" || !isAudienceTraitOption(option)) {
    return undefined;
  }
  const column = additionalColumns?.find(
    (additionalColumn) => additionalColumn.alias === optionValue.columnAlias,
  )?.column;
  return column?.type === "static_value" ? column : undefined;
}

/** The icon and model name for an option's source cell in mixed views. */
export function getOptionSourceMeta(
  option: ColumnOption,
  additionalColumns?: AdditionalColumn[],
): (IconMeta & { name: string }) | undefined {
  const entity = option.entity;
  if (!entity) return undefined;
  // A static audience trait is set on the audience, which the API reports as its model.
  const meta = findStaticAudienceTrait(option, additionalColumns)
    ? AUDIENCE_SOURCE_META
    : getSourceIconMeta(entity);
  return meta && { ...meta, name: entity.name };
}

function getSourceIconMeta(
  entity: NonNullable<ColumnOption["entity"]>,
): IconMeta | undefined {
  // A split's source is the audience being split, not a model.
  if (entity.isSplit) return AUDIENCE_SOURCE_META;
  if (entity.isJourneyContextVariable) {
    return COLUMN_CATEGORY_META["journey-context-variables"];
  }
  if (entity.modelType === "model") return COLUMN_CATEGORY_META.model;
  if (entity.modelType === "related") return COLUMN_CATEGORY_META.related;
  if (entity.modelType === "journey-event") {
    return COLUMN_CATEGORY_META["journey-event"];
  }
  return undefined;
}

export function filterColumnOptions(
  options: ColumnOption[],
  search: string,
): ColumnOption[] {
  if (!search) return options;
  const regex = getSearchRegExp(search, "i");
  return options.filter(
    (option) =>
      regex.test(option.label) || regex.test(option.entity?.name ?? ""),
  );
}

/** The id of the category holding `from`, never "all". */
export function findCategoryIdForValue(
  categories: ColumnCategory[],
  from: unknown,
): string | undefined {
  if (from === undefined || from === null) return undefined;
  const target = columnReferenceKey(from);
  for (const c of categories) {
    if (c.kind === "all") continue;
    if (
      c.options.some((option) => columnReferenceKey(option.value) === target)
    ) {
      return c.id;
    }
  }
  return undefined;
}
